import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import logger from "@/lib/logger";

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error("[ErrorBoundary] Render exception:", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Reset error state whenever the route changes so the user can navigate away.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { onGoHome } = this.props;
    const message = this.state.error?.message || "An unexpected error occurred while rendering this page.";

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-12" data-testid="error-boundary-fallback">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-zinc-900 mb-2" data-testid="error-boundary-title">
            Something went wrong
          </h2>
          <p className="text-sm text-zinc-500 mb-6" data-testid="error-boundary-message">
            {message} Please refresh the page or head back to your dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              data-testid="error-boundary-refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={() => {
                this.handleReset();
                onGoHome?.();
              }}
              data-testid="error-boundary-home-btn"
            >
              <Home className="w-4 h-4 mr-2" /> Go to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default function ErrorBoundary({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <ErrorBoundaryInner resetKey={location.pathname} onGoHome={() => navigate("/dashboard")}>
      {children}
    </ErrorBoundaryInner>
  );
}
